/**
 * 明細行の割引計算ユーティリティ (functions / admin-web 共用)。
 *
 * 割引の仕様 (2026-07-08):
 *  - 割引は行単位 (単価 × 数量 の税抜小計) に適用する
 *  - amount: 税抜の固定額。percent: 割引率 (0〜100)
 *  - 税込小計は税抜と同率で減額する (割引なしの行は従来計算と完全一致)
 *
 * フロントのプレビューとバックエンドの保存値が食い違わないよう、
 * 見積・請求の行金額は必ずこの関数を通して計算すること。
 */
import type { LineDiscountType } from '../types/quote';

export interface LineAmountsInput {
  /** 単価 (税抜) */
  priceExcludingTax: number;
  /** 単価 (税込) */
  priceIncludingTax: number;
  quantity: number;
  discountType?: LineDiscountType | null;
  discountValue?: number | null;
}

export interface LineAmounts {
  /** 割引前の行小計 (税抜) */
  baseExcludingTax: number;
  /** 割引前の行小計 (税込) */
  baseIncludingTax: number;
  /** 割引額 (税抜)。0 = 割引なし */
  discountAmountExcludingTax: number;
  /** 割引後の行小計 (税抜) */
  subtotalExcludingTax: number;
  /** 割引後の行小計 (税込) */
  subtotalIncludingTax: number;
  /**
   * 入力された割引が行小計を超えていたため小計まで切り詰めた (= 行が 0 円になった)。
   * 「割引欄に商品価格を入力してしまう」誤入力を UI 側で検知するためのフラグ (2026-08-22)。
   */
  discountClamped: boolean;
}

/** 割引入力を正規化する。無効値 / 0 以下は「割引なし (null)」に落とす。 */
export function normalizeLineDiscount(
  discountType: unknown,
  discountValue: unknown,
): { discountType: LineDiscountType | null; discountValue: number | null } {
  const type =
    discountType === 'amount' || discountType === 'percent'
      ? discountType
      : null;
  const value =
    type !== null &&
    typeof discountValue === 'number' &&
    isFinite(discountValue) &&
    discountValue > 0
      ? type === 'percent'
        ? Math.min(discountValue, 100)
        : Math.round(discountValue)
      : null;
  return value === null
    ? { discountType: null, discountValue: null }
    : { discountType: type, discountValue: value };
}

/** 1 行分の金額 (割引額・割引後小計) を計算する。 */
export function calcLineAmounts(input: LineAmountsInput): LineAmounts {
  const qty = Math.max(1, Math.floor(input.quantity || 1));
  const baseExcludingTax = Math.round(input.priceExcludingTax * qty);
  const baseIncludingTax = Math.round(input.priceIncludingTax * qty);

  const { discountType, discountValue } = normalizeLineDiscount(
    input.discountType,
    input.discountValue,
  );
  let discountAmountExcludingTax = 0;
  let discountClamped = false;
  if (discountType !== null && discountValue !== null) {
    const requested =
      discountType === 'percent'
        ? Math.round((baseExcludingTax * discountValue) / 100)
        : discountValue;
    // 行小計を超える割引は行小計まで (マイナス行は作らない)
    discountAmountExcludingTax = Math.min(requested, baseExcludingTax);
    discountClamped = requested > baseExcludingTax;
  }

  const subtotalExcludingTax = baseExcludingTax - discountAmountExcludingTax;
  // 税込は税抜と同率で減額 (割引なし・税抜 0 円の行は従来値をそのまま維持)
  const subtotalIncludingTax =
    discountAmountExcludingTax === 0 || baseExcludingTax === 0
      ? baseIncludingTax
      : Math.round((subtotalExcludingTax * baseIncludingTax) / baseExcludingTax);

  return {
    baseExcludingTax,
    baseIncludingTax,
    discountAmountExcludingTax,
    subtotalExcludingTax,
    subtotalIncludingTax,
    discountClamped,
  };
}

/**
 * 割引の表示ラベル (例: '¥50,000' / '10%')。割引なしは null。
 * 書類・一覧の「割引」表記に使う。
 */
export function lineDiscountLabel(
  discountType: LineDiscountType | null | undefined,
  discountValue: number | null | undefined,
): string | null {
  const norm = normalizeLineDiscount(discountType, discountValue);
  if (norm.discountType === null || norm.discountValue === null) return null;
  return norm.discountType === 'percent'
    ? `${norm.discountValue}%`
    : `¥${norm.discountValue.toLocaleString('ja-JP')}`;
}
